/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosText, EdsIcon, ResponsiveContainer, SectionContainer } from "@components/elements/atoms";
import { CosmosDesc } from "@components/elements/molecules/title-desc-stack";
import { IndustryFragment, NewsletterSignupFragment } from "@cosmos/hygraph-schema";
import { css } from "@panda/css";
import { VStack } from "@panda/jsx";
import { IndustrySelector } from "./IndustrySelector";
import { NewsletterSignupButton } from "./NewsletterSignup.button";
import { useEffect, useState } from "react";
import { useLocale, useMessages } from "@epic-mw/i18n";
import { useAccountAODCPolicy, useIsLoggedIn, useIsOptIn, useSaveIndustry, useUpdateOptIn } from "@cosmos/next-hooks";
import { useLocation } from "@epic-mw/app-context";
import { scrollIntoViewEffect } from "@components/utils/scroll.utils";

const styles = css({
	maxWidth: "860px",
	marginInline: "auto",
	textAlign: "center",
});

export const NewsletterSignup = ({
	signUpLabel,
	industryPlaceholder,
	industriesRef,
	successTitle,
	successDescription,
}: NewsletterSignupFragment): React.JSX.Element => {
	const [submit, setSubmit] = useState(false);
	const location = useLocation();

	useEffect(() => {
		if (location.hash === "#newsletter-signup") {
			return scrollIntoViewEffect("newsletter-signup");
		}
		return;
	}, [location]);

	let showIndustry = false;
	if (industryPlaceholder && industriesRef?.industries.length) {
		showIndustry = true;
	}
	const paramsSubscribe = location.searchParams.get("subscribe") || "";
	let initIndustry = null;
	if (paramsSubscribe && showIndustry) {
		const match = industriesRef?.industries.find((ind) => {
			return ind.salesForceId === paramsSubscribe;
		});
		if (match && match.salesForceId) {
			initIndustry = match;
		}
	}

	const [selectedIndustry, setSelectedIndustry] = useState<IndustryFragment | null>(initIndustry);
	const locale = useLocale();
	const { hasOptIn } = useIsOptIn();
	const { isOptInSuccess, isLoading: isLoadingOptIn, error: isOptInFailed } = useUpdateOptIn(submit);
	const { isLoading: isLoadingDataRouter } = useSaveIndustry(
		{
			industry_v1: selectedIndustry?.salesForceId,
			industry_v2: selectedIndustry?.dataRouterId,
		},
		submit,
	);
	const isLoading = isLoadingOptIn || isLoadingDataRouter;

	const isLoggedIn = useIsLoggedIn();
	const { result: accountPolicy, isLoading: isLoadingPolicy } = useAccountAODCPolicy();

	/*
	  FAIL CLOSED, but only for someone the policy actually applies to.

	  Anything that is not a definite "passed" blocks the signup form, so an
	  indeterminate consent lookup no longer reads as consent given. This was
	  `accountPolicy?.AODCFailed || false`, which turned both "the request failed"
	  and "the body was malformed" into false, i.e. into "allowed". That is the
	  fail-open MW-13431 closes.

	  `isLoggedIn` is load-bearing and NOT redundant. `useAccountAODCPolicy` only
	  fetches when there is an account id, so logged out the SWR key is null, which
	  leaves `result` undefined AND `isLoadingPolicy` false — because that flag is
	  `Boolean(!data && !error && shouldFetch)`. Without this guard the expression is
	  true for every anonymous visitor, and the blocked layout replaces the sign-in
	  CTA on every site. AODC consent is a property of an account; there is nothing
	  to fail closed on when nobody is signed in.

	  It also covers the account-bootstrap window: while the account is resolving the
	  id is empty, so the form serves; once it resolves, `isLoadingPolicy` is true and
	  the form still serves until the lookup settles. No blocked-state flash either
	  way.

	  Watch the `unknown` share of `cosmos.policy.aodc_lookup`. The revert trigger
	  and the one-line rollback are documented on AODC_LOOKUP_METRIC in
	  aodc.hooks.ts.
	*/
	const isAodcFailed = isLoggedIn && !isLoadingPolicy && accountPolicy?.status !== "passed";

	const validIndustrySelected = Boolean(showIndustry && selectedIndustry?.salesForceId);
	const messages = useMessages();

	const notAllowed = messages["epic.cosmos.newsletter.not.allowed.title"] || "";
	const nowAllowedDesc = messages["epic.cosmos.newsletter.not.allowed.message"] || "";
	const failedDesc = messages["epic.cosmos.newsletter.failed.message"] || "";
	const optInDesc = messages["epic.cosmos.newsletter.globaloptin"] || "";
	const privacyPolicyLabel = messages["epic.cosmos.privacypolicy"] || "";
	const privacyPolicyHtml = `${optInDesc || ""}  <a href="https://www.epicgames.com/site/${locale}/privacypolicy">${privacyPolicyLabel}</a>`;

	const signupLayout = (
		<>
			<CosmosText variant={"headingXL"}>
				{signUpLabel}{" "}
				{showIndustry ? (
					<IndustrySelector
						industryPlaceholder={industryPlaceholder}
						industries={industriesRef?.industries || []}
						selectedIndustry={selectedIndustry}
						setSelectedIndustry={setSelectedIndustry}
						validIndustrySelected={validIndustrySelected}
					/>
				) : null}
			</CosmosText>

			<NewsletterSignupButton
				showIndustry={showIndustry}
				isLoading={isLoading}
				selectedIndustry={selectedIndustry}
				setSubmit={setSubmit}
				disabled={!validIndustrySelected}
			/>

			{!isLoading ? (
				<CosmosDesc className={css({ maxWidth: "460px" })} description={{ html: privacyPolicyHtml, variant: "uiSM" }} />
			) : null}
		</>
	);

	const aodcLayout = (
		<>
			<CosmosText variant={"headingXL"}>{notAllowed}</CosmosText>
			<CosmosText variant="uiMD" text={nowAllowedDesc} color="eds.text.secondary" />
		</>
	);

	const alreadyOptInLayout = (
		<>
			<CosmosText variant={"headingXL"}>{successTitle}</CosmosText>
			<EdsIcon icon="Check" gradient="blue" size="lg" />
		</>
	);

	const successOptInLayout = (
		<>
			<CosmosText variant={"headingXL"}>{successTitle}</CosmosText>
			<CosmosDesc className={css({ maxWidth: "460px" })} description={{ html: successDescription }} />
			<EdsIcon icon="Check" gradient="blue" size="lg" />
		</>
	);

	const errorOptInLayout = (
		<>
			<CosmosText variant={"headingXL"}>{notAllowed}</CosmosText>
			<CosmosDesc className={css({ maxWidth: "460px" })} description={{ html: failedDesc }} />
		</>
	);

	let content = signupLayout;
	if (isAodcFailed) {
		content = aodcLayout;
	} else if (isOptInSuccess) {
		content = successOptInLayout;
	} else if (hasOptIn) {
		content = alreadyOptInLayout;
	} else if (isOptInFailed) {
		content = errorOptInLayout;
	}
	return (
		<ResponsiveContainer id="newsletter-signup">
			<SectionContainer>
				<VStack alignItems="center" gap="eds.dimension.24" className={`newsletter-signup-section ${styles}`}>
					<EdsIcon icon="Envelope" gradient="blue" size="xxl" />
					{content}
				</VStack>
			</SectionContainer>
		</ResponsiveContainer>
	);
};
